import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Upload, Utensils, TrendingUp, Zap, Apple, X, Eye, Trash2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';

const MealAnalyzer = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('analyze');
  
  const [formData, setFormData] = useState({
    meal_name: '',
    meal_time: 'lunch',
    notes: ''
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMealHistory();
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const fetchMealHistory = async () => {
    try {
      const response = await axios.get(`${API}/meals/history?days=30`);
      setMeals(response.data.meals || []);
    } catch (error) {
      console.error('Error fetching meals:', error);
    }
  };

  const startCamera = async () => {
    try {
      setVideoReady(false);
      // Try rear camera first, fall back to any camera
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
      } catch (envError) {
        // Fall back to any available camera
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
      }
      
      setCameraStream(stream);
      setShowCamera(true);
      
      // Wait for dialog to render, then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(() => {
              setVideoReady(true);
            }).catch(err => {
              console.error('Video play error:', err);
              toast.error(t('meal.cameraError') || 'Failed to start video');
            });
          };
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error(t('messages.cameraAccessDenied') || 'Camera access denied. Please enable camera permissions.');
    }
  };

  const capturePhoto = () => {
    try {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      if (!video || !canvas) {
        toast.error(t('meal.captureError') || 'Camera not ready');
        return;
      }
      
      // Check if video has valid dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        toast.error(t('meal.videoNotReady') || 'Video not ready. Please wait a moment.');
        return;
      }
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error(t('meal.captureError') || 'Failed to capture photo');
          return;
        }
        
        const file = new File([blob], `meal-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedImage(file);
        setImagePreview(URL.createObjectURL(blob));
        stopCamera();
        toast.success(t('meal.photoCaptured') || 'Photo captured successfully!');
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Capture error:', error);
      toast.error(t('meal.captureError') || 'Failed to capture photo');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setVideoReady(false);
    setShowCamera(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const analyzeMeal = async () => {
    if (!selectedImage) {
      toast.error(t('meal.selectPhoto'));
      return;
    }

    setAnalyzing(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', selectedImage);
      formDataObj.append('meal_time', formData.meal_time);
      formDataObj.append('meal_name', formData.meal_name);
      formDataObj.append('notes', formData.notes);

      const response = await axios.post(`${API}/meals/analyze`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setCurrentAnalysis(response.data.analysis);
      setShowAnalysis(true);
      toast.success(t('meal.analysisComplete'));
      
      // Reset form
      setSelectedImage(null);
      setImagePreview(null);
      setFormData({ meal_name: '', meal_time: 'lunch', notes: '' });
      
      // Refresh history
      fetchMealHistory();
    } catch (error) {
      toast.error(t('meal.analysisFailed'));
    } finally {
      setAnalyzing(false);
    }
  };

  const viewMealDetails = async (mealId) => {
    try {
      const response = await axios.get(`${API}/meals/${mealId}`);
      setCurrentAnalysis(response.data.meal.analysis);
      setShowAnalysis(true);
    } catch (error) {
      toast.error(t('meal.loadFailed'));
    }
  };

  const deleteMeal = async (mealId) => {
    if (!window.confirm(t('meal.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API}/meals/${mealId}`);
      toast.success(t('meal.deleted'));
      fetchMealHistory();
    } catch (error) {
      toast.error(t('meal.deleteFailed'));
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />

      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
            <Utensils className="w-10 h-10 text-green-600" />
            {t('meal.title')}
          </h1>
          <p className="text-gray-600 mt-2">{t('meal.subtitle')}</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="analyze">
              <Camera className="w-4 h-4 mr-2" />
              {t('meal.analyzeNew')}
            </TabsTrigger>
            <TabsTrigger value="history">
              <Utensils className="w-4 h-4 mr-2" />
              {t('meal.history')} ({meals.length})
            </TabsTrigger>
          </TabsList>

          {/* Analyze Tab */}
          <TabsContent value="analyze">
            <Card className="p-8 bg-white shadow-xl">
              <div className="space-y-6">
                {/* Image Upload/Capture */}
                {!imagePreview ? (
                  <div className="space-y-4">
                    <Label>{t('meal.uploadPhoto')}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="p-8 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all text-center"
                      >
                        <Upload className="w-12 h-12 text-green-600 mx-auto mb-2" />
                        <p className="font-medium text-gray-700">{t('meal.selectFile')}</p>
                        <p className="text-sm text-gray-500 mt-1">{t('meal.supportedFormats')}</p>
                      </div>
                      <div
                        onClick={startCamera}
                        className="p-8 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all text-center"
                      >
                        <Camera className="w-12 h-12 text-green-600 mx-auto mb-2" />
                        <p className="font-medium text-gray-700">{t('meal.takePhoto')}</p>
                        <p className="text-sm text-gray-500 mt-1">{t('meal.useCamera')}</p>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>{t('meal.selectedPhoto')}</Label>
                    <div className="relative mt-2">
                      <img
                        src={imagePreview}
                        alt="Selected meal"
                        className="w-full h-64 object-cover rounded-lg border border-gray-200 bg-gray-100"
                      />
                      <Button
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedImage(null);
                        }}
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Meal Details */}
                {imagePreview && (
                  <>
                    <div>
                      <Label htmlFor="meal_name">{t('meal.mealName')}</Label>
                      <Input
                        id="meal_name"
                        value={formData.meal_name}
                        onChange={(e) => setFormData({ ...formData, meal_name: e.target.value })}
                        placeholder={t('meal.mealNamePlaceholder')}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="meal_time">{t('meal.mealTime')}</Label>
                      <select
                        id="meal_time"
                        value={formData.meal_time}
                        onChange={(e) => setFormData({ ...formData, meal_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md mt-1"
                      >
                        <option value="breakfast">{t('meal.breakfast')}</option>
                        <option value="lunch">{t('meal.lunch')}</option>
                        <option value="dinner">{t('meal.dinner')}</option>
                        <option value="snack">{t('meal.snack')}</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="notes">{t('meal.notes')}</Label>
                      <textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t('meal.notesPlaceholder')}
                        className="w-full px-3 py-2 border rounded-md mt-1"
                        rows="3"
                      />
                    </div>

                    <Button
                      onClick={analyzeMeal}
                      disabled={analyzing}
                      className="w-full py-6 text-lg bg-gradient-to-r from-green-600 to-teal-600"
                    >
                      {analyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {t('meal.analyzing')}
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          {t('meal.analyze')}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            {meals.length === 0 ? (
              <Card className="p-12 text-center">
                <Utensils className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('meal.noMeals')}</h3>
                <p className="text-gray-600">{t('meal.startAnalyzing')}</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {meals.map((meal) => (
                  <Card key={meal.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img
                      src={`data:image/jpeg;base64,${meal.image_data}`}
                      alt={meal.meal_name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-gray-800">{meal.meal_name}</h3>
                          <p className="text-sm text-gray-500 capitalize">{meal.meal_time}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getHealthScoreColor(meal.analysis.health_score)}`}>
                          {meal.analysis.health_score}/10
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Zap className="w-4 h-4" />
                          {meal.analysis.estimated_calories} cal
                        </span>
                        <span>{new Date(meal.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => viewMealDetails(meal.id)}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('meal.view')}
                        </Button>
                        <Button
                          onClick={() => deleteMeal(meal.id)}
                          variant="outline"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Camera Dialog */}
        <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('meal.capturePhoto') || 'Capture Meal Photo'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
                {!videoReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mb-3"></div>
                    <p className="text-sm">{t('meal.initializingCamera') || 'Initializing camera...'}</p>
                  </div>
                )}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                />
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-2">
                <Button 
                  onClick={capturePhoto} 
                  disabled={!videoReady}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600"
                >
                  {!videoReady ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('meal.waitingForCamera') || 'Waiting...'}
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      {t('meal.capture') || 'Capture'}
                    </>
                  )}
                </Button>
                <Button onClick={stopCamera} variant="outline" className="flex-1">
                  {t('buttons.cancel') || 'Cancel'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                {t('meal.cameraInstructions') || 'Position your meal in the frame and tap Capture'}
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Analysis Dialog */}
        <Dialog open={showAnalysis} onOpenChange={setShowAnalysis}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('meal.analysisResults')}</DialogTitle>
            </DialogHeader>
            {currentAnalysis && (
              <div className="space-y-4">
                {/* Health Score */}
                <Card className={`p-4 ${getHealthScoreColor(currentAnalysis.health_score)}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{t('meal.healthScore')}</span>
                    <span className="text-2xl font-bold">{currentAnalysis.health_score}/10</span>
                  </div>
                </Card>

                {/* Calories */}
                <Card className="p-4 bg-gradient-to-r from-orange-50 to-red-50">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">{t('meal.calories')}</span>
                    <span className="text-2xl font-bold text-orange-600">
                      {currentAnalysis.estimated_calories}
                    </span>
                  </div>
                </Card>

                {/* Macros */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('meal.macros')}</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <Card className="p-3 text-center bg-blue-50">
                      <p className="text-2xl font-bold text-blue-600">{currentAnalysis.macros.protein_g}g</p>
                      <p className="text-xs text-gray-600">{t('meal.protein')}</p>
                    </Card>
                    <Card className="p-3 text-center bg-yellow-50">
                      <p className="text-2xl font-bold text-yellow-600">{currentAnalysis.macros.carbs_g}g</p>
                      <p className="text-xs text-gray-600">{t('meal.carbs')}</p>
                    </Card>
                    <Card className="p-3 text-center bg-purple-50">
                      <p className="text-2xl font-bold text-purple-600">{currentAnalysis.macros.fat_g}g</p>
                      <p className="text-xs text-gray-600">{t('meal.fat')}</p>
                    </Card>
                  </div>
                </div>

                {/* Identified Foods */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('meal.identifiedFoods')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentAnalysis.identified_foods?.map((food, index) => (
                      <span key={`item-${index}`} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        {food}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Analysis */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('meal.analysis')}</h3>
                  <p className="text-gray-700 text-sm">{currentAnalysis.analysis}</p>
                </div>

                {/* Suggestions */}
                {currentAnalysis.suggestions && currentAnalysis.suggestions.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">{t('meal.suggestions')}</h3>
                    <ul className="space-y-2">
                      {currentAnalysis.suggestions.map((suggestion, index) => (
                        <li key={`item-${index}`} className="flex items-start gap-2 text-sm text-gray-700">
                          <Apple className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MealAnalyzer;
